import { useState, useMemo, useEffect, useCallback, Fragment, lazy, Suspense } from 'react';
import { loadSettings, saveSettings } from '../utils/settingsStore';
import { useDashboardConfig, useGeographyOverride, useSetGeographyOverride } from '../context/DashboardContext';
import { useCensusData } from '../data/useCensusData';
import { useCDCData } from '../data/useCDCData';
import { useSVIData } from '../data/useSVIData';
import { useEJScreenData } from '../data/useEJScreenData';
import { useEducationData } from '../data/useEducationData';
import { INDICATOR_REGISTRY } from '../config/indicatorRegistry';
import Scorecard from './Scorecard';
import TrendView from './TrendView';
import LeftPanel from './LeftPanel';
import DashboardSettings from './DashboardSettings';
import GeographyPanel from './GeographyPanel';
import AdditionalGeographyPanel from './AdditionalGeographyPanel';
import DataErrorBadge from './ErrorBanner';
import LoadingSpinner from './LoadingSpinner';
import { useCountyList } from '../data/useCountyList';
import { useComparisonData } from '../data/useComparisonData';
import { useSelectedTractTrend } from '../data/useSelectedTractTrend';
import { getGradient } from '../config/gradients';
import { groupIndicators } from '../utils/groupIndicators';
import { PRESET_THEMES } from './ThemeTab';
import IntroScreen from './IntroScreen';
import EngagementView from './EngagementView';

const MapView = lazy(() => import('./MapView'));

export default function DashboardShell() {
  const config = useDashboardConfig();
  const geographyOverride = useGeographyOverride();
  const setGeographyOverride = useSetGeographyOverride();
  const [geoPanelOpen, setGeoPanelOpen] = useState(false);
  const [layersPanelOpen, setLayersPanelOpen] = useState(false);
  const [activeLayers, setActiveLayers] = useState(new Set());
  const [infoOpen, setInfoOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // ── Brand theme ────────────────────────────────────────────────────────────
  const [brandTheme, setBrandTheme] = useState(() => {
    try { return localStorage.getItem('community_dashboard_brand_theme') ?? 'fcp'; } catch (_) { return 'fcp'; }
  });
  const [customBranding, setCustomBranding] = useState(() => {
    try {
      const stored = localStorage.getItem('community_dashboard_custom_branding');
      return stored ? JSON.parse(stored) : { primaryColor: '#2d6b49', accentColor: '#c8614a', backgroundColor: '#f0fbff', logoDataUrl: null };
    } catch (_) { return { primaryColor: '#2d6b49', accentColor: '#c8614a', backgroundColor: '#f0fbff', logoDataUrl: null }; }
  });

  // ── Intro screen ──────────────────────────────────────────────────────────
  const [introContent, setIntroContent] = useState(() => {
    try {
      const stored = localStorage.getItem('community_dashboard_intro_content');
      return stored ? JSON.parse(stored) : { enabled: false, title: '', body: '', contentKey: '' };
    } catch (_) { return { enabled: false, title: '', body: '', contentKey: '' }; }
  });
  const [introVisible, setIntroVisible] = useState(() => {
    try {
      const stored = localStorage.getItem('community_dashboard_intro_content');
      if (!stored) return false;
      const c = JSON.parse(stored);
      if (!c.enabled) return false;
      const dismissed = localStorage.getItem('community_dashboard_intro_dismissed_key') ?? '';
      return dismissed !== c.contentKey;
    } catch (_) { return false; }
  });

  // ── Selected indicators (up to 5) — the live source of truth ──────────────
  const [selectedIndicators, setSelectedIndicators] = useState(() => {
    // If the config version has changed (or was never stored), seed all localStorage
    // keys from config.defaultSettings so every browser starts from the same state.
    // Must run before any other state initializer so their reads see the seeded values.
    try {
      const storedVersion = localStorage.getItem('community_dashboard_config_version');
      const configVersion = String(config.configVersion ?? 1);
      if (storedVersion !== configVersion && config.defaultSettings) {
        const ds = config.defaultSettings;
        const seedIndicators = (ds.indicatorIds ?? [])
          .map(id => INDICATOR_REGISTRY.find(r => r.id === id))
          .filter(Boolean);
        if (seedIndicators.length > 0)
          localStorage.setItem('community_dashboard_selected_indicators', JSON.stringify(seedIndicators));
        localStorage.setItem('community_dashboard_enabled',    JSON.stringify(ds.enabledIds   ?? []));
        localStorage.setItem('community_dashboard_goals',      JSON.stringify(ds.goals        ?? {}));
        localStorage.setItem('community_dashboard_themes',     JSON.stringify(ds.themes       ?? []));
        localStorage.setItem('community_dashboard_gradients',  JSON.stringify(ds.gradients    ?? {}));
        localStorage.setItem('community_dashboard_rationales', JSON.stringify(ds.rationales   ?? {}));
        localStorage.setItem('community_dashboard_year_range', JSON.stringify(ds.yearRange    ?? { from: null, to: null }));
        localStorage.setItem('community_dashboard_config_version', configVersion);
      }
    } catch (_) {}

    try {
      const stored = localStorage.getItem('community_dashboard_selected_indicators');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Prefer fresh registry entry per ID; fall back to stored object for non-registry indicators
          const reconstructed = parsed.map(item => {
            const id = item?.id ?? item;
            return INDICATOR_REGISTRY.find(r => r.id === id) ?? (typeof item === 'object' ? item : null);
          }).filter(Boolean);
          if (reconstructed.length > 0) return reconstructed;
        }
      }
    } catch (_) {}
    // Fall back to defaultIndicatorIds → registry entries, or config.indicators
    if (config.defaultIndicatorIds) {
      const fromRegistry = config.defaultIndicatorIds
        .map(id => INDICATOR_REGISTRY.find(r => r.id === id))
        .filter(Boolean);
      if (fromRegistry.length > 0) return fromRegistry;
    }
    return config.indicators;
  });

  // ── Enabled / visible indicator IDs (admin toggle) ────────────────────────
  const [enabledIndicatorIds, setEnabledIndicatorIds] = useState(() => {
    try {
      const stored = localStorage.getItem('community_dashboard_enabled');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (_) {}
    return selectedIndicators.map(i => i.id);
  });

  const [goals, setGoals] = useState(() => {
    try {
      const stored = localStorage.getItem('community_dashboard_goals');
      return stored ? JSON.parse(stored) : {};
    } catch (_) { return {}; }
  });

  const [gradients, setGradients] = useState(() => {
    try {
      const stored = localStorage.getItem('community_dashboard_gradients');
      return stored ? JSON.parse(stored) : {};
    } catch (_) { return {}; }
  });

  const [rationales, setRationales] = useState(() => {
    try {
      const stored = localStorage.getItem('community_dashboard_rationales');
      return stored ? JSON.parse(stored) : {};
    } catch (_) { return {}; }
  });

  const [yearRange, setYearRange] = useState(() => {
    try {
      const stored = localStorage.getItem('community_dashboard_year_range');
      return stored ? JSON.parse(stored) : { from: null, to: null };
    } catch (_) { return { from: null, to: null }; }
  });

  const [themes, setThemes] = useState(() => {
    try {
      const stored = localStorage.getItem('community_dashboard_themes');
      return stored ? JSON.parse(stored) : [];
    } catch (_) { return []; }
  });

  const [storageWarning, setStorageWarning] = useState(false);
  // Wraps localStorage.setItem — catches QuotaExceededError and surfaces a warning
  const lsSet = useCallback((key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      if (e instanceof DOMException && (e.name === 'QuotaExceededError' || e.code === 22)) {
        setStorageWarning(true);
      }
    }
  }, []);

  // ── Data hooks use selectedIndicators and geographyOverride as sources of truth ──
  const { data: censusData, loading: censusLoading, error: censusError } =
    useCensusData(config, selectedIndicators, geographyOverride);
  const { data: cdcData, loading: cdcLoading, error: cdcError } =
    useCDCData(config, selectedIndicators, geographyOverride);
  const { data: sviData, loading: sviLoading, error: sviError } =
    useSVIData(config, selectedIndicators, geographyOverride);
  const { data: ejscreenData, loading: ejscreenLoading, error: ejscreenError } =
    useEJScreenData(config, selectedIndicators, geographyOverride);
  const { data: educationData, loading: educationLoading, error: educationError } =
    useEducationData(config, selectedIndicators, geographyOverride);

  // Merge all data sources into a single Map — components receive this instead of
  // separate censusData/cdcData props, keeping the prop surface flat as sources grow.
  const allData = useMemo(() => new Map([
    ...(censusData    || new Map()),
    ...(cdcData       || new Map()),
    ...(sviData       || new Map()),
    ...(ejscreenData  || new Map()),
    ...(educationData || new Map()),
  ]), [censusData, cdcData, sviData, ejscreenData, educationData]);

  // ── Year helpers ──────────────────────────────────────────────────────────
  const availableYears = useMemo(() => {
    const yearSet = new Set();
    selectedIndicators.forEach(ind => (ind.years ?? []).forEach(y => yearSet.add(y)));
    return [...yearSet].sort((a, b) => b - a);
  }, [selectedIndicators]);

  // null = "Latest Available" — each indicator shows its own most recent data
  const [selectedYear, setSelectedYear] = useState(null);

  const visibleYears = useMemo(() => {
    return availableYears.filter(y => {
      if (yearRange.from !== null && y < yearRange.from) return false;
      if (yearRange.to !== null && y > yearRange.to) return false;
      return true;
    });
  }, [availableYears, yearRange]);

  // Clamp selectedYear to visibleYears when the range changes
  // null = "Latest Available" — never clamp it
  useEffect(() => {
    if (selectedYear !== null && visibleYears.length > 0 && !visibleYears.includes(selectedYear)) {
      setSelectedYear(visibleYears[0]);
    }
  }, [visibleYears, selectedYear]);

  // ── Enabled indicators derived from selectedIndicators ────────────────────
  const enabledIndicators = useMemo(
    () => selectedIndicators.filter(i => enabledIndicatorIds.includes(i.id)),
    [selectedIndicators, enabledIndicatorIds]
  );

  const indicatorSections = useMemo(
    () => groupIndicators(enabledIndicators, themes),
    [enabledIndicators, themes]
  );

  const [activeView, setActiveView] = useState('scorecard');
  const [selectedTract, setSelectedTract] = useState(null);
  // null | 'outcomes' | 'customize'
  const [settingsPanel, setSettingsPanel] = useState(null);
  const [comparisonCounty, setComparisonCounty] = useState(null);
  const [compareInput, setCompareInput] = useState('');

  // ── Load settings from Supabase on mount ──────────────────────────────────
  // localStorage is already applied (synchronous, instant); Supabase overrides
  // it as the shared source of truth once the async fetch resolves (~200ms).
  const applyRemoteSettings = useCallback((data) => {
    if (!data) return;
    if (Array.isArray(data.indicatorIds) && data.indicatorIds.length > 0) {
      const rebuilt = data.indicatorIds
        .map(id => INDICATOR_REGISTRY.find(r => r.id === id))
        .filter(Boolean);
      if (rebuilt.length > 0) {
        setSelectedIndicators(rebuilt);
        lsSet('community_dashboard_selected_indicators', JSON.stringify(rebuilt));
      }
    }
    if (data.enabledIds) {
      setEnabledIndicatorIds(data.enabledIds);
      lsSet('community_dashboard_enabled', JSON.stringify(data.enabledIds));
    }
    if (data.goals)      { setGoals(data.goals);           lsSet('community_dashboard_goals',      JSON.stringify(data.goals)); }
    if (data.themes)     { setThemes(data.themes);         lsSet('community_dashboard_themes',     JSON.stringify(data.themes)); }
    if (data.gradients)  { setGradients(data.gradients);   lsSet('community_dashboard_gradients',  JSON.stringify(data.gradients)); }
    if (data.rationales) { setRationales(data.rationales); lsSet('community_dashboard_rationales', JSON.stringify(data.rationales)); }
    if (data.yearRange)  { setYearRange(data.yearRange);   lsSet('community_dashboard_year_range', JSON.stringify(data.yearRange)); }
    if (data.brandTheme) { setBrandTheme(data.brandTheme); lsSet('community_dashboard_brand_theme', data.brandTheme); }
    if (data.customBranding) { setCustomBranding(data.customBranding); lsSet('community_dashboard_custom_branding', JSON.stringify(data.customBranding)); }
    if (data.introContent) {
      setIntroContent(data.introContent);
      lsSet('community_dashboard_intro_content', JSON.stringify(data.introContent));
      const dismissed = localStorage.getItem('community_dashboard_intro_dismissed_key') ?? '';
      if (data.introContent.enabled && dismissed !== data.introContent.contentKey) {
        setIntroVisible(true);
      }
    }
  }, [lsSet]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadSettings().then(applyRemoteSettings);
  }, [applyRemoteSettings]);

  // Reset tract and comparison when geography changes
  useEffect(() => {
    setSelectedTract(null);
    setComparisonCounty(null);
  }, [geographyOverride]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only show comparison for Mode 1 (single county)
  const showComparison = !geographyOverride || geographyOverride.mode === 'single';

  const { counties } = useCountyList(config);
  const { data: comparisonData } = useComparisonData(
    config,
    showComparison ? (comparisonCounty?.fips ?? null) : null,
    selectedIndicators
  );
  const tractTrendData = useSelectedTractTrend(config, selectedTract);

  const currentGradient = useMemo(
    () => getGradient(gradients[activeView]),
    [gradients, activeView]
  );

  const isLoading = censusLoading || cdcLoading || sviLoading || ejscreenLoading || educationLoading;
  const errors = [censusError, cdcError, sviError, ejscreenError, educationError].filter(Boolean);
  const { org } = config;

  // Compute effective branding from active theme
  const effectiveBranding = useMemo(() => {
    const preset = PRESET_THEMES.find(t => t.id === brandTheme);
    if (preset) return preset;
    // Custom theme
    return {
      primaryColor: customBranding?.primaryColor ?? '#2d6b49',
      accentColor: customBranding?.accentColor ?? '#c8614a',
      backgroundColor: customBranding?.backgroundColor ?? '#f0fbff',
      fontHeading: 'Playfair Display',
      fontBody: 'Inter',
      logoSrc: customBranding?.logoDataUrl ?? '/logo-fcp.svg',
      logoAlt: 'Logo'
    };
  }, [brandTheme, customBranding]);

  const isIndicatorView = enabledIndicatorIds.includes(activeView);

  function handleTabClick(viewId) {
    setActiveView(viewId);
    if (viewId !== activeView) setSelectedTract(null);
  }

  function handleAdminSave(ids, newGoals, newGradients, newRationales, newYearRange, newThemes, reorderedIndicators) {
    lsSet('community_dashboard_enabled', JSON.stringify(ids));
    lsSet('community_dashboard_goals', JSON.stringify(newGoals));
    lsSet('community_dashboard_gradients', JSON.stringify(newGradients));
    lsSet('community_dashboard_rationales', JSON.stringify(newRationales));
    lsSet('community_dashboard_year_range', JSON.stringify(newYearRange));
    lsSet('community_dashboard_themes', JSON.stringify(newThemes));
    setEnabledIndicatorIds(ids);
    setGoals(newGoals);
    setGradients(newGradients);
    setRationales(newRationales);
    setYearRange(newYearRange);
    setThemes(newThemes);
    if (reorderedIndicators) {
      setSelectedIndicators(reorderedIndicators);
      lsSet('community_dashboard_selected_indicators', JSON.stringify(reorderedIndicators));
    }
    if (!ids.includes(activeView)) setActiveView(ids[0]);
    const indicatorIds = (reorderedIndicators ?? selectedIndicators).map(i => i.id);
    saveSettings({ indicatorIds, enabledIds: ids, goals: newGoals, themes: newThemes, gradients: newGradients, rationales: newRationales, yearRange: newYearRange });
  }

  function handleOutcomeSave(newIndicators) {
    const ids = newIndicators.map(i => i.id);
    lsSet('community_dashboard_selected_indicators', JSON.stringify(newIndicators));
    lsSet('community_dashboard_enabled', JSON.stringify(ids));
    // Also persist current customizations so they survive a reload even if the
    // user only used the Outcomes tab (without visiting the Customize tab).
    lsSet('community_dashboard_goals', JSON.stringify(goals));
    lsSet('community_dashboard_gradients', JSON.stringify(gradients));
    lsSet('community_dashboard_rationales', JSON.stringify(rationales));
    lsSet('community_dashboard_year_range', JSON.stringify(yearRange));
    lsSet('community_dashboard_themes', JSON.stringify(themes));
    setSelectedIndicators(newIndicators);
    setEnabledIndicatorIds(ids);
    setActiveView(ids[0] ?? 'scorecard');
    saveSettings({ indicatorIds: ids, enabledIds: ids, goals, themes, gradients, rationales, yearRange });
  }

  function handleIntroSave(content) {
    // Bump contentKey so users who dismissed will see the updated screen again
    const updated = { ...content, contentKey: Date.now().toString() };
    setIntroContent(updated);
    lsSet('community_dashboard_intro_content', JSON.stringify(updated));
    // Clear per-browser dismissed flag so the screen shows on next load
    localStorage.removeItem('community_dashboard_intro_dismissed_key');
    saveSettings({ indicatorIds: selectedIndicators.map(i => i.id), enabledIds: enabledIndicatorIds, goals, themes, gradients, rationales, yearRange, brandTheme, customBranding, introContent: updated });
  }

  function handleThemeSave(theme, custom) {
    setBrandTheme(theme);
    setCustomBranding(custom);
    lsSet('community_dashboard_brand_theme', theme);
    lsSet('community_dashboard_custom_branding', JSON.stringify(custom));
    saveSettings({ indicatorIds: selectedIndicators.map(i => i.id), enabledIds: enabledIndicatorIds, goals, themes, gradients, rationales, yearRange, brandTheme: theme, customBranding: custom });
  }

  // Keep compareInput in sync when comparisonCounty is reset externally
  useEffect(() => {
    setCompareInput(comparisonCounty?.name ?? '');
  }, [comparisonCounty]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleCompareInput(val) {
    setCompareInput(val);
    if (!val.trim()) { setComparisonCounty(null); return; }
    const match = counties.find(c => c.name.toLowerCase() === val.toLowerCase());
    if (match) setComparisonCounty(match);
  }

  function handleCompareBlur() {
    // If input doesn't match any county, snap back to the last valid selection
    if (compareInput.trim() && !counties.find(c => c.name.toLowerCase() === compareInput.toLowerCase())) {
      setCompareInput(comparisonCounty?.name ?? '');
    }
  }

  function handleLayerToggle(id) {
    setActiveLayers(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleGeographyApply(override) {
    setGeographyOverride(override);
    setGeoPanelOpen(false);
  }

  return (
    <div
      className="dashboard-shell"
      style={{
        '--primary': effectiveBranding.primaryColor,
        '--accent': effectiveBranding.accentColor,
        '--bg': effectiveBranding.backgroundColor,
        '--font-heading': `'${effectiveBranding.fontHeading}', serif`,
        '--font-body': `'${effectiveBranding.fontBody}', sans-serif`
      }}
    >
      <header className="dashboard-header">
        <div className="header-inner">
          <img src={effectiveBranding.logoSrc} alt={effectiveBranding.logoAlt} className="header-logo" />
          <div className="header-info-wrap">
            <button
              className="header-info-btn"
              onClick={() => setInfoOpen(v => !v)}
              aria-label="About this dashboard"
              title="About this dashboard"
            >
              ⓘ
            </button>
            {infoOpen && (
              <div className="header-info-popover" role="dialog" aria-label="About this dashboard">
                <button className="header-info-close" onClick={() => setInfoOpen(false)} aria-label="Close">×</button>
                <p>
                  Community data dashboard built by{' '}
                  <a href="https://north-arrow.org" target="_blank" rel="noopener noreferrer">North Arrow</a>
                  {' '}for{' '}
                  <a href="https://findingcommonpurpose.org" target="_blank" rel="noopener noreferrer">Finding Common Purpose</a>.
                </p>
              </div>
            )}
          </div>
          <div className="header-geo-row">
            <span className="header-geo">{org.geography.label}</span>
            <button
              className="header-county-edit"
              onClick={() => setGeoPanelOpen(true)}
              title="Change geography"
            >
              ✎
            </button>
            <button
              className="header-layers-btn"
              onClick={() => setLayersPanelOpen(true)}
              title="Toggle overlay layers"
            >
              ⊞
            </button>
          </div>

          <div className="header-filters">
            {visibleYears.length > 0 && (
              <div className="header-year">
                <label className="header-compare-label" htmlFor="year-select">Year:</label>
                <select
                  id="year-select"
                  className="header-compare-select"
                  value={selectedYear ?? ''}
                  onChange={e => setSelectedYear(e.target.value === '' ? null : Number(e.target.value))}
                >
                  <option value="">Latest Available</option>
                  {visibleYears.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            )}

            {showComparison && (
              <div className="header-compare">
                <label className="header-compare-label" htmlFor="county-compare-input">
                  Compare to:
                </label>
                <input
                  id="county-compare-input"
                  className="header-compare-input"
                  list="county-compare-list"
                  placeholder="None"
                  value={compareInput}
                  onChange={e => handleCompareInput(e.target.value)}
                  onBlur={handleCompareBlur}
                  autoComplete="off"
                />
                <datalist id="county-compare-list">
                  {counties.map(c => (
                    <option key={c.fips} value={c.name} />
                  ))}
                </datalist>
                {comparisonCounty && (
                  <button
                    className="header-compare-clear"
                    onClick={() => handleCompareInput('')}
                    aria-label="Clear comparison"
                    title="Clear comparison"
                  >×</button>
                )}
              </div>
            )}
          </div>

          {/* Mobile-only filter toggle — hidden on desktop via CSS */}
          <button
            className="mobile-filter-btn"
            onClick={() => setMobileFiltersOpen(v => !v)}
            aria-label="Filters"
          >
            {mobileFiltersOpen ? '▲' : '▼'} Filters
          </button>
        </div>

        {/* Mobile filter drawer — slides down from header, hidden on desktop */}
        {mobileFiltersOpen && (
          <div className="mobile-filter-drawer">
            {visibleYears.length > 0 && (
              <div className="header-year">
                <label className="header-compare-label" htmlFor="year-select-m">Year:</label>
                <select
                  id="year-select-m"
                  className="header-compare-select"
                  value={selectedYear ?? ''}
                  onChange={e => {
                    setSelectedYear(e.target.value === '' ? null : Number(e.target.value));
                    setMobileFiltersOpen(false);
                  }}
                >
                  <option value="">Latest Available</option>
                  {visibleYears.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            )}
            {showComparison && (
              <div className="header-compare">
                <label className="header-compare-label" htmlFor="county-compare-input-m">
                  Compare to:
                </label>
                <input
                  id="county-compare-input-m"
                  className="header-compare-input"
                  list="county-compare-list-m"
                  placeholder="None"
                  value={compareInput}
                  onChange={e => handleCompareInput(e.target.value)}
                  onBlur={handleCompareBlur}
                  autoComplete="off"
                />
                <datalist id="county-compare-list-m">
                  {counties.map(c => (
                    <option key={c.fips} value={c.name} />
                  ))}
                </datalist>
                {comparisonCounty && (
                  <button
                    className="header-compare-clear"
                    onClick={() => handleCompareInput('')}
                    aria-label="Clear comparison"
                    title="Clear comparison"
                  >×</button>
                )}
              </div>
            )}
          </div>
        )}
      </header>

      {storageWarning && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
          background: '#c8614a', color: 'white', padding: '0.5rem 1rem',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontSize: '0.875rem', fontFamily: 'var(--font-body)'
        }}>
          <span>Storage full — your settings could not be saved locally. They are still saved to the server if connected.</span>
          <button
            onClick={() => setStorageWarning(false)}
            style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1rem', padding: '0 0.5rem' }}
            aria-label="Dismiss"
          >×</button>
        </div>
      )}

      <div className={`dashboard-content${isIndicatorView ? '' : ' dashboard-content--full'}`}>

        {isLoading && isIndicatorView && (
          <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }}>
            <LoadingSpinner label="Loading community data…" />
          </div>
        )}

        {isIndicatorView ? (
          <>
            <LeftPanel
              selectedIndicatorId={activeView}
              selectedTract={selectedTract}
              allData={allData}
              indicators={selectedIndicators}
              comparisonCounty={comparisonCounty}
              comparisonData={comparisonData}
              selectedYear={selectedYear}
              onYearSelect={setSelectedYear}
              tractTrendData={tractTrendData}
              goal={goals[activeView] ?? null}
              rationale={rationales[activeView] ?? null}
              visibleYears={visibleYears}
            />
            <Suspense fallback={<LoadingSpinner label="Loading map…" />}>
              <MapView
                allData={allData}
                indicators={selectedIndicators}
                selectedIndicatorId={activeView}
                selectedTractGeoid={selectedTract?.geoid ?? null}
                onTractSelect={setSelectedTract}
                gradient={currentGradient}
                selectedYear={selectedYear}
                comparisonCounty={showComparison ? comparisonCounty : null}
                comparisonData={showComparison ? comparisonData : null}
                geographyOverride={geographyOverride}
                activeLayers={activeLayers}
              />
            </Suspense>
          </>
        ) : activeView === 'scorecard' ? (
          <div className="scorecard-view">
            {isLoading && <LoadingSpinner label="Loading community data…" />}
            {!isLoading && (
              <Scorecard
                allData={allData}
                selectedTract={selectedTract}
                enabledIndicators={enabledIndicators}
                indicatorSections={indicatorSections}
                comparisonCounty={showComparison ? comparisonCounty : null}
                comparisonData={showComparison ? comparisonData : null}
                selectedYear={selectedYear}
                onYearSelect={setSelectedYear}
                goals={goals}
                onIndicatorSelect={handleTabClick}
              />
            )}
          </div>
        ) : activeView === 'trends' ? (
          <>
            {isLoading
              ? <LoadingSpinner label="Loading community data…" />
              : <TrendView
                  allData={allData}
                  indicators={enabledIndicators}
                  selectedYear={selectedYear}
                  comparisonCounty={showComparison ? comparisonCounty : null}
                  comparisonData={showComparison ? comparisonData : null}
                  visibleYears={visibleYears}
                />
            }
          </>
        ) : activeView === 'community' ? (
          <EngagementView
            allData={allData}
            indicators={enabledIndicators}
            gradients={gradients}
            selectedYear={selectedYear}
          />
        ) : null}
      </div>

      <DataErrorBadge errors={errors} />

      <nav className="bottom-tabs" aria-label="Dashboard views">
        <button
          className={`bottom-tab bottom-tab--scorecard${activeView === 'scorecard' ? ' bottom-tab--active' : ''}`}
          onClick={() => handleTabClick('scorecard')}
        >
          Scorecard
        </button>

        {indicatorSections.map(section => (
          <Fragment key={section.theme ?? '__ungrouped__'}>
            {section.theme && (
              <span className="tab-theme-label">{section.theme}</span>
            )}
            {section.indicators.map(indicator => (
              <button
                key={indicator.id}
                className={`bottom-tab${activeView === indicator.id ? ' bottom-tab--active' : ''}`}
                onClick={() => handleTabClick(indicator.id)}
              >
                {indicator.label}
              </button>
            ))}
          </Fragment>
        ))}

        <button
          className={`bottom-tab bottom-tab--trends${activeView === 'trends' ? ' bottom-tab--active' : ''}`}
          onClick={() => handleTabClick('trends')}
        >
          ⊞ Data
        </button>

        <button
          className={`bottom-tab bottom-tab--community${activeView === 'community' ? ' bottom-tab--active' : ''}`}
          onClick={() => handleTabClick('community')}
        >
          ♟ Community
        </button>

        <button
          className="bottom-tab bottom-tab--admin"
          onClick={() => setSettingsPanel('outcomes')}
          aria-label="Dashboard settings"
          title="Dashboard settings"
        >
          ⚙
        </button>
      </nav>

      {settingsPanel && (
        <DashboardSettings
          initialTab={settingsPanel}
          selectedIndicators={selectedIndicators}
          onOutcomesSave={handleOutcomeSave}
          indicators={selectedIndicators}
          currentEnabled={enabledIndicatorIds}
          currentGoals={goals}
          currentGradients={gradients}
          currentRationales={rationales}
          currentYearRange={yearRange}
          allYears={availableYears}
          currentThemes={themes}
          onCustomizeSave={handleAdminSave}
          currentBrandTheme={brandTheme}
          currentCustomBranding={customBranding}
          onThemeSave={handleThemeSave}
          currentIntroContent={introContent}
          onIntroSave={handleIntroSave}
          onClose={() => setSettingsPanel(null)}
        />
      )}

      {geoPanelOpen && (
        <GeographyPanel
          currentOverride={geographyOverride}
          defaultState={config.org.geography.state}
          onApply={handleGeographyApply}
          onClose={() => setGeoPanelOpen(false)}
        />
      )}

      {layersPanelOpen && (
        <AdditionalGeographyPanel
          activeLayers={activeLayers}
          onToggle={handleLayerToggle}
          onClose={() => setLayersPanelOpen(false)}
        />
      )}

      {introVisible && (
        <IntroScreen
          title={introContent.title}
          body={introContent.body}
          onClose={dontShow => {
            setIntroVisible(false);
            if (dontShow) {
              lsSet('community_dashboard_intro_dismissed_key', introContent.contentKey);
            }
          }}
        />
      )}
    </div>
  );
}
